import cx from 'classnames';
import { FunctionComponent, useRef, useState } from 'react';

import useOnClickOutside from '../../../../hooks/click-outside';
import Chevrelon from '../../../ui/icons/cheverlon';
import ListItem from '../../../ui/v2/vehicle-list/list-item';
import { modelAtom, versionAtom } from '../../../../hooks/use-picker-selection';
import { useAtom } from 'jotai';
import {
  currentSavedCarAtom,
  selectVehicleStepAtom,
  unsavedInformationAtom,
  vehiclePickerOpen,
} from '../../../../state/vehicle';
import { useRouter } from 'next/router';
import useLinkProps from '../../../../hooks/use-link-props';

type TCompleteList = {
  headerTag?: keyof JSX.IntrinsicElements;
  itemTag?: keyof JSX.IntrinsicElements;
  values: TListModelsOrVersions;
  displayList?: boolean;
  numberOfColumns?: number;
  customClickFunction?: boolean;
  listType?: 'MODELS' | 'VERSIONS' | undefined;
};

const CompleteList: FunctionComponent<TCompleteList> = ({
  headerTag: HeaderTag = 'h3',
  itemTag = 'h4',
  values,
  displayList = false,
  numberOfColumns = 1,
  customClickFunction,
  listType,
}) => {
  const ref = useRef(null);
  const { push, query, route } = useRouter();
  const getLink = useLinkProps();
  const [currentSelect, setCurrentSelect] = useState<string | null>(null);
  useOnClickOutside(ref, () => setCurrentSelect(null));
  const [, setModel] = useAtom(modelAtom);
  const [, setVersion] = useAtom(versionAtom);
  const [, setSelectVehicleStep] = useAtom(selectVehicleStepAtom);
  const [, setOpen] = useAtom(vehiclePickerOpen);
  const [, setCurrentSavedCar] = useAtom(currentSavedCarAtom);
  const itemRef = useRef();
  const [, setUnsavedInformation] = useAtom(unsavedInformationAtom);

  const customOnClickFunction = (selectedValue) => {
    if (!customClickFunction) return;

    if (listType === 'MODELS') {
      setModel(selectedValue);
      if (selectedValue.have_versions) {
        setSelectVehicleStep('SELECT_VERSION');
        setUnsavedInformation(false);
        return;
      }
    }

    if (listType === 'VERSIONS') {
      setVersion(selectedValue);
    }

    if (route !== '/') {
      const {
        reference,
        category,
        subCategory,
        priceFrom,
        priceTo,
        page,
        ...restTokeep
      } = query;

      const link = getLink('search', {
        //...restTokeep,
        ...(reference && { reference }),
        ...(priceFrom && { priceFrom }),
        ...(priceTo && { priceTo }),
        ...(category && { category }),
        ...(page && { page }),
        ...(subCategory && { subCategory }),
        ...(selectedValue && { vehicle: selectedValue.slug }),
      });
      setUnsavedInformation(false);
      setOpen(false);
      setCurrentSavedCar(null);
      setSelectVehicleStep(null);
      push(link.href, link.as, { shallow: true, scroll: true });
    } else {
      setSelectVehicleStep('SELECT_PART');
      setUnsavedInformation(false);
      return;
    }
  };

  return (
    <ul
      ref={ref}
      className={cx(
        displayList
          ? 'grid grid-cols-1 gap-2'
          : listType === 'MODELS'
            ? 'lg:grid lg:grid-cols-4 lg:gap-6'
            : 'lg:grid lg:grid-cols-6 lg:gap-6'
      )}
    >
      {Object.entries(values).map(([key, links]) => {
        return (
          <li
            key={key}
            className={cx(
              'group relative',
              'block w-full',
              'bg-white',
              'border-b',
              {
                'border-b-white shadow-lg z-11 max-lg:z-9':
                  currentSelect === key,
                'hover:border-brand-logo': currentSelect !== key,
                'hover:border-b-neutral-80': currentSelect !== key,
              }
            )}
            id={`scroll_into_${key}`}
          >
            <button
              onClick={() => {
                //document
                //.getElementById(`scroll_into_${key}`)
                //?.scrollIntoView({ behavior: 'smooth' });
                return currentSelect === key
                  ? setCurrentSelect(null)
                  : setCurrentSelect(key);
              }}
              className={cx(
                {
                  'text-brand-logo': currentSelect === key,
                },
                {
                  'shadow-lg': currentSelect === key,
                },
                'group-hover:text-brand-logo hover:bg-brand-10',
                'w-full',
                'py-1'
              )}
            >
              <HeaderTag
                className={cx(
                  'flex justify-between items-center',
                  'px-4 w-full font-bold'
                )}
              >
                <span className={cx('block', 'py-2')}>{key}</span>
                <Chevrelon
                  className={cx(
                    'w-3',
                    'transition-transform duration-300 ease-in-out',
                    {
                      'rotate-180': currentSelect === key,
                    }
                  )}
                />
              </HeaderTag>
            </button>
            <ul
              className={cx(
                {
                  hidden: !currentSelect || currentSelect !== key,
                },
                'bg-white',
                'shadow-lg',
                {
                  'flex flex-col gap-2': displayList,
                  'lg:z-[1] lg:max-h-48 lg:absolute w-full overflow-auto':
                    !displayList,
                }
              )}
            >
              {links.map((entry) => (
                <ListItem
                  itemTag={itemTag}
                  displayList={displayList}
                  value={entry}
                  key={entry.id}
                  customClickFunction={customClickFunction}
                  onClick={() => customOnClickFunction(entry)}
                />
              ))}
            </ul>
          </li>
        );
      })}
    </ul>
  );
};

export default CompleteList;
